import React, { FC, useState } from "react";
import { object, string } from "yup";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import TextField from "../../components/TextField/TextField";
import Button from "../../components/Button/Button";
import { authAPI, AddWhitelistUserRequest } from "../../services/api";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess: () => void;
}

const schema = object({
  email: string()
    .required("Email is required")
    .email("Please enter a valid email address"),
});

const AddWhitelistUser: FC<IProps> = ({ open, setOpen, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: schema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        console.log('AddWhitelistUser: Starting API call with values:', values);
        
        const addData: AddWhitelistUserRequest = {
          email: values.email,
        };

        console.log('AddWhitelistUser: API payload:', addData);
        const response = await authAPI.addWhitelistUser(addData);
        console.log('AddWhitelistUser: API response:', response);
        
        // Show success message
        toast.success(response.message || 'User added to whitelist successfully!');
        
        // Close modal
        handleClose();
        
        // Refresh whitelist data
        onSuccess();
        
      } catch (err: any) {
        console.log('AddWhitelistUser: API error:', err);
        console.log('AddWhitelistUser: Error response:', err.response);
        
        let errorMessage = 'Failed to add user to whitelist. Please try again.';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.status === 400) {
          errorMessage = 'Invalid email address. Please check and try again.';
        } else if (err.response?.status === 409) {
          errorMessage = 'This email is already in the whitelist.';
        } else if (err.response?.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        console.log('AddWhitelistUser: Final error message:', errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[536px] p-[25px] bg-white rounded-[15px] relative">
        <div
          className="absolute top-[25px] right-[25px] rounded-full size-[30px] bg-[#0000000D] cursor-pointer grid place-items-center"
          onClick={handleClose}
        >
          <img src={ASSETS.cross} alt="" />
        </div>
        <Typography variant="h2" className="font-bold title">
          Add User to Whitelist
        </Typography>
        <Typography
          variant="body1"
          color="grey"
          className="mt-[5px] font-medium"
        >
          Enter the email address of the user you want to add to the whitelist.
        </Typography>
        <div className="border border-k-border rounded-lg p-6 mt-[25px] flex flex-col gap-6">
          <TextField
            label="Email Address"
            name="email"
            type="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : ""
            }
            onClear={() => formik.setFieldValue("email", "")}
            required
          />
          <Button
            label={loading ? "Adding..." : "Add User"}
            variant="primary"
            className="w-full h-[45px] disabled:opacity-200 disabled:cursor-not-allowed disabled:!text-gray-400"
            onClick={() => {
              console.log('AddWhitelistUser: Button clicked');
              console.log('AddWhitelistUser: Form values:', formik.values);
              console.log('AddWhitelistUser: Form errors:', formik.errors);
              console.log('AddWhitelistUser: Form touched:', formik.touched);
              formik.handleSubmit();
            }}
            loading={loading}
            disabled={loading}
            type="button"
          />
        </div>
      </div>
    </DialogLayout>
  );
};

export default AddWhitelistUser;
